import FontAwesomeIcon from "app/components/layouts/Icons/FontAwesome";
import { faVolumeUp } from "@fortawesome/free-solid-svg-icons";
import { Vocabulary, Pronunciation } from 'types/vocabulary';
import * as vocabularyApi from 'app/requests/vocabularies';
import React, { useState, useEffect } from 'react';
import { getColumn } from 'app/utils/translate';
import Router, { useRouter } from "next/router";
import first from 'lodash/first';
import env from '.env.json';

const DictionaryTooltip = () => {
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null);
  const [pronunciation, setPronunciation] = useState<Pronunciation | null>(null);
  const [music, setMusic] = useState<HTMLAudioElement | null>(null);
  const router = useRouter();

  // Function to position the tooltip
  const positionTooltip = (e) => {
    const offsetfromcursorY = 10;
    const offsetfromcursorX = 12;
    const rightedge = window.innerWidth - 20 - e.clientX - offsetfromcursorX;
    const bottomedge = window.innerHeight - 20 - e.clientY - offsetfromcursorY;

    const tooltipWidth = 200; // Estimated tooltip width
    const tooltipHeight = 200; // Estimated tooltip height

    let x = e.pageX + offsetfromcursorX;
    let y = e.pageY + offsetfromcursorY;

    // Adjust position to prevent overflow
    if (rightedge < tooltipWidth) {
      x = e.pageX - tooltipWidth;
    }

    if (bottomedge < tooltipHeight) {
      y = e.pageY - tooltipHeight - offsetfromcursorY;
    }

    setTooltipPosition({ x, y });
  };

  // Function to load the dictionary for the word
  const openDictionary = async (word: string) => {
    const vocabulary = await vocabularyApi.getSummary(word, router?.locale ?? env.DEFAULT_LOCALE) as Vocabulary;

    if (vocabulary) {
      const sound = first(vocabulary?.pronunciations);
      const audioUrl = `${env.URL_API_FILE}/${sound?.audioFile}`;

      setPronunciation(sound)
      setMusic(new Audio(audioUrl));
      setVocabulary(vocabulary);
    }
  };

  // Double-click event handler
  const handleDoubleClick = (e: Event) => {
    const text = window.getSelection()?.toString() || '';
    const regex = /^[A-Za-z]+$/;
    const isEnglishWord = regex.test(text);

    if (isEnglishWord && text) {
      positionTooltip(e);
      openDictionary(text.toLowerCase());
    }
  };

  // Close the tooltip
  const closeTooltip = () => {
    setVocabulary(null);
    setPronunciation(null);
    setMusic(null);
  };

  useEffect(() => {
    const routerView = document.getElementById('RouterViewWeb');

    if (routerView) {
      routerView.ondblclick = handleDoubleClick;

      // Add click event to close tooltip
      routerView.addEventListener('click', () => {
        closeTooltip();
      });
    }

    Router.events.on('routeChangeStart', closeTooltip)
    return () => {
      if (routerView) {
        routerView.removeEventListener('click', closeTooltip);
        routerView.ondblclick = null;
      }
    };
  }, [router?.locale]);

  return (
    <>
      {vocabulary && (
        <div
          id="dictionaryTooltip"
          style={{
            position: 'absolute',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            visibility: vocabulary ? 'visible' : 'hidden',
            background: '#fff',
            border: '1px solid #000',
            zIndex: 1000,
            width: '300px',
          }}
        >
          <div style={{ borderBottom: '1px solid #000', background: '#1BB0F6' }}>
            <table border={0} style={{ width: '100%', background: '#1BB0F6', height: '30px' }}>
              <tbody>
                <tr>
                  <td width="60" className="pb-1">
                    <a href={env.ROOT_URL} target="_blank" rel="noopener noreferrer">
                      <img
                        alt="Logo"
                        width="120px"
                        src={`/images/commons/logos/${env.DEFAULT_LOCALE == 'vi' ? 'logo.webp' : 'englook.png'}`}
                      />
                    </a>
                  </td>
                  <td className="pb-1">&nbsp;</td>
                  <td width="20" className="pb-1">
                    <img
                      src="/images/commons/close.webp"
                      alt="Close"
                      style={{ cursor: 'pointer' }}
                      onClick={closeTooltip}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            style={{ width: '100%', height: '200px', border: 0 }}
            className='soft-scroll overflow-auto p-2'
          >
            <div>
              <b style={{ color: '#FF0000' }} className="text-size-mmd pr-2">
                {vocabulary.word}
              </b>
              {pronunciation &&
                <span className='mb-0 mt-3 mr-2'>
                  <FontAwesomeIcon
                    icon={faVolumeUp}
                    onClick={() => music?.play()}
                    className='ml-2 cursor-pointer'
                  />
                  <span className="text-secondary">
                    /{pronunciation?.phoneticSpelling?.replaceAll('/', '')}/
                  </span>
                </span>
              }
            </div>
            {vocabulary?.summary &&
              <div style={{ maxHeight: 300 }} className='summary'>
                <div className='pb-1 text-size-sm' dangerouslySetInnerHTML={{
                  __html: getColumn(vocabulary?.summary, router?.locale)
                }}></div>
              </div>
            }
          </div>
          <style jsx>{`
            #dictionary {
              position: absolute;
              left: -300px;
              width: 300px;
              border: 1px solid black;
              background-color: #fff;
              visibility: hidden;
              z-index: 100;
            }
          `}
          </style>
        </div>
      )}
    </>
  );
};

export default DictionaryTooltip;
